import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBiometricAuth } from '@/hooks/useBiometricAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Fingerprint, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

// Déploiement dédié Inside VNX (vnx.valnexo.fr) → après connexion, rester sur Inside VNX (pas le CRM)
const getPostLoginPath = () =>
    (typeof window !== 'undefined' && /^(vnx|inside)\./i.test(window.location.hostname)) ? '/inside-vnx' : '/dashboard';

// Logo Component - Image officielle Valnexo
const ValnexoLogo = ({ size = 50 }) => (
    <img 
        src="/valnexo-logo-official-new.png"
        alt="Valnexo Logo"
        width={size}
        height={size}
        style={{ objectFit: 'contain', borderRadius: 6 }}
    />
);

export default function Login() {
    const navigate = useNavigate();
    const { login, setAuthData } = useAuth();
    const { 
        isAvailable: biometricAvailable, 
        isEnabled: biometricEnabled,
        isLoading: biometricLoading,
        isMobile,
        authenticateWithBiometric,
        saveSessionForBiometric
    } = useBiometricAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);

    // Normalisation défensive côté client (miroir du backend) : retire espaces,
    // NBSP et caractères invisibles collés depuis un e-mail ou injectés par un
    // password manager. On ne touche PAS aux espaces au milieu.
    const INVISIBLE_RE = /^[\s\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u3000\uFEFF]+|[\s\u00A0\u1680\u180E\u2000-\u200D\u202F\u205F\u3000\uFEFF]+$/g;
    const cleanCredential = (s) => (s == null ? '' : String(s).replace(INVISIBLE_RE, ''));

    // Check for biometric login on mount (mobile only)
    useEffect(() => {
        if (biometricEnabled && isMobile) {
            setShowBiometricPrompt(true);
        }
    }, [biometricEnabled, isMobile]);

    // Nettoyage systématique de toute session résiduelle à l'arrivée sur /login.
    // Objectif : garantir qu'aucun token/utilisateur d'une session précédente ne
    // contamine la requête d'auth (ex : Jeremy encore identifié qui essaie de se
    // connecter avec le compte de Virgile). Défensif : sur certains navigateurs
    // avec extensions, un Authorization Bearer traînant peut faire échouer la
    // requête au niveau réseau (RESET, blocage, CORS strict).
    useEffect(() => {
        try {
            localStorage.removeItem('valnexo_token');
            localStorage.removeItem('valnexo_user');
        } catch (e) { /* localStorage indisponible : rien à faire */ }
    }, []);

    // 🔓 GARANTIE CONNEXION : purge préventive de TOUT Service Worker actif
    // au chargement de /login. Détail critique : `unregister()` NE LIBÈRE PAS
    // le SW qui contrôle déjà l'onglet en cours — il continue d'intercepter les
    // requêtes jusqu'à un reload complet. On DOIT donc reload la page si un SW
    // contrôle actuellement l'onglet, sinon le login continue d'être intercepté
    // par le zombie. Le flag `?fresh=1` empêche une boucle infinie.
    useEffect(() => {
        (async () => {
            try {
                if (!('serviceWorker' in navigator)) return;
                const params = new URLSearchParams(window.location.search);
                const alreadyFresh = params.get('fresh') === '1';
                const hasController = navigator.serviceWorker.controller !== null;
                if (hasController && !alreadyFresh) {
                    // Un SW contrôle l'onglet ET on n'a pas encore fait le reload → nettoie et reload.
                    // On fait ça AVANT que l'user tape quoi que ce soit, donc pas de perte de saisie.
                    console.warn('[LOGIN] SW controller détecté, purge + reload forcé pour libérer la session…');
                    try {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
                    } catch (_) { /* ignore */ }
                    if ('caches' in window) {
                        try {
                            const keys = await caches.keys();
                            await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
                        } catch (_) { /* ignore */ }
                    }
                    // Reload avec le flag anti-boucle. window.location.replace pour ne pas
                    // laisser l'entrée sans flag dans l'historique (sinon back → boucle).
                    window.location.replace('/login?fresh=1');
                    return;
                }
                // Cas nominal : pas de SW controller → simple purge silencieuse au cas où
                // il y aurait un SW en waiting (nouvelle version). Pas besoin de reload.
                if (!hasController) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    if (regs.length > 0) {
                        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
                    }
                }
            } catch (_) { /* best-effort : ne bloque jamais l'affichage du formulaire */ }
        })();
    }, []);

    // Handle biometric authentication
    const handleBiometricLogin = async () => {
        const result = await authenticateWithBiometric();
        if (result.success && result.token) {
            // Restore session from biometric
            try {
                setAuthData(result.token);
                toast.success('Connexion biométrique réussie');
                navigate(getPostLoginPath());
            } catch (e) {
                toast.error('Session expirée, veuillez vous reconnecter');
                setShowBiometricPrompt(false);
            }
        } else {
            toast.error(result.error || 'Authentification échouée');
            setShowBiometricPrompt(false);
        }
    };

    // Fallback fetch natif : si axios rate au niveau réseau (extension navigateur,
    // proxy corporate, SW zombie…), on retente la même requête directement via
    // `fetch()` — pas d'intercepteur, pas de header hérité. C'est le dernier filet
    // pour offrir une expérience « ça juste marche ».
    const loginViaFetch = async (email, password) => {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const url = `${base}/api/auth/login`;
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'omit',
            cache: 'no-store',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            const err = new Error(data?.detail || 'Login échoué');
            err.response = { status: res.status, data };
            throw err;
        }
        return data;
    };

    // Fallback XHR : dernier recours si axios ET fetch échouent tous les deux.
    // XMLHttpRequest utilise une pile réseau différente et échappe à certaines
    // extensions/proxies qui bloquent fetch. Aussi utilisé sur les vieux Android.
    const loginViaXHR = (email, password) => new Promise((resolve, reject) => {
        try {
            const base = process.env.REACT_APP_BACKEND_URL || '';
            const url = `${base}/api/auth/login`;
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            xhr.timeout = 30000;
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('Cache-Control', 'no-store');
            xhr.onload = () => {
                let data = {};
                try { data = JSON.parse(xhr.responseText || '{}'); } catch (_) { /* body non-JSON */ }
                if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
                const err = new Error(data?.detail || `HTTP ${xhr.status}`);
                err.response = { status: xhr.status, data };
                reject(err);
            };
            xhr.onerror = () => reject(new Error('XHR network error'));
            xhr.ontimeout = () => reject(new Error('XHR timeout'));
            xhr.send(JSON.stringify({ email, password }));
        } catch (e) { reject(e); }
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        // Normalisation défensive : retire espaces/NBSP/zero-width avant envoi.
        const cleanEmail = cleanCredential(email).toLowerCase();
        const cleanPwd = cleanCredential(password);
        try {
            let response;
            try {
                response = await login(cleanEmail, cleanPwd);
            } catch (axErr) {
                // Si axios remonte "pas de réponse" (network/SW/extension), on retente
                // via fetch natif. Si ça fonctionne, on installe la session comme si
                // c'était un login normal.
                if (!axErr.response && !axErr.code?.includes('ECONN')) {
                    // Timeout ne rentre pas ici, seulement les vraies erreurs réseau.
                }
                if (!axErr.response) {
                    console.warn('[LOGIN] axios sans réponse, fallback via fetch()');
                    let fallbackData = null;
                    try {
                        fallbackData = await loginViaFetch(cleanEmail, cleanPwd);
                    } catch (fetchErr) {
                        // Si fetch a une réponse HTTP (401 par ex.), on la propage telle quelle
                        if (fetchErr?.response) throw fetchErr;
                        // Fetch a échoué au niveau réseau aussi → dernier recours : XHR
                        console.warn('[LOGIN] fetch() sans réponse, fallback ultime via XMLHttpRequest');
                        fallbackData = await loginViaXHR(cleanEmail, cleanPwd);
                    }
                    if (fallbackData?.access_token) {
                        // On stocke la session directement (sans repasser par /auth/me
                        // qui utiliserait à nouveau axios — le SW/extension pourrait
                        // encore bloquer). Le prochain reload la rechargera proprement.
                        try {
                            localStorage.setItem('valnexo_token', fallbackData.access_token);
                            if (fallbackData.user) {
                                localStorage.setItem('valnexo_user', JSON.stringify(fallbackData.user));
                            }
                        } catch (e) { /* localStorage indispo */ }
                        response = fallbackData;
                    } else {
                        throw axErr;
                    }
                } else {
                    throw axErr;
                }
            }

            // Save session for biometric reauth if enabled
            if (biometricEnabled && response?.access_token) {
                saveSessionForBiometric(response.access_token);
            }

            setFailedAttempts(0);
            toast.success('Connexion réussie');
            navigate(getPostLoginPath());
        } catch (error) {
            // On incrémente le compteur d'échecs pour proposer le reset après 2 tentatives.
            const isAuthError = error.response?.status === 401 || error.response?.status === 403;
            if (isAuthError) setFailedAttempts((n) => n + 1);
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                toast.error('Le serveur met trop de temps à répondre. Patientez quelques secondes et réessayez.');
            } else if (!error.response) {
                // Aucune réponse HTTP après axios + fallback fetch : problème réseau réel.
                // Diagnostic en console pour analyse à distance.
                console.error('[LOGIN] Aucune réponse HTTP après fallback. Détails:', {
                    message: error?.message,
                    code: error?.code,
                    name: error?.name,
                });
                // ⚠️ NE PLUS purger le SW / caches automatiquement à chaque échec :
                // ça créait un cycle de purge/réinstallation à chaque blip réseau et
                // effaçait le mot de passe tapé par l'user à cause du reload.
                // On affiche un message clair et on incrémente le compteur : après 2
                // échecs, le bloc d'aide propose le bouton « Réinitialiser le cache »
                // que l'user déclenche à la main, en connaissance de cause.
                toast.error("Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.", { duration: 5000 });
                setFailedAttempts((n) => n + 1);
            } else if (error.response?.status === 401) {
                toast.error(error.response?.data?.detail || 'Email ou mot de passe incorrect');
            } else if (error.response?.status >= 500) {
                toast.error('Erreur temporaire du serveur. Réessayez dans quelques instants.');
            } else {
                toast.error(error.response?.data?.detail || 'Erreur de connexion');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Branding with Valnexo theme */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0B1F3B' }}>
                {/* Background image with overlay */}
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBoYXBweSUyMGNvbGxhYm9yYXRpb24lMjBzdWNjZXNzJTIwbmV0d29ya2luZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzA5MzMxNDl8MA&ixlib=rb-4.1.0&q=85')`,
                        opacity: 0.15
                    }}
                />
                
                {/* Gradient overlay */}
                <div 
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, rgba(11,31,59,0.95) 0%, rgba(11,31,59,0.8) 50%, rgba(30,91,255,0.3) 100%)'
                    }}
                />
                
                {/* Decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div 
                        className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl"
                        style={{ backgroundColor: '#1E5BFF' }}
                    />
                    <div 
                        className="absolute bottom-20 left-20 w-80 h-80 rounded-full opacity-10 blur-3xl"
                        style={{ backgroundColor: '#F2B705' }}
                    />
                </div>
                
                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    {/* Logo */}
                    <div className="flex items-center gap-4">
                        <ValnexoLogo size={55} />
                        <div>
                            <h1 className="font-bold text-2xl tracking-wide" style={{ color: '#F5F7FA' }}>VALNEXO</h1>
                            <p className="text-sm tracking-widest font-medium" style={{ color: '#1E5BFF' }}>CRM ÉNERGIE</p>
                        </div>
                    </div>
                    
                    {/* Main message */}
                    <div className="space-y-8">
                        <h2 className="text-4xl font-bold leading-tight" style={{ color: '#F5F7FA' }}>
                            L'union des talents,
                            <br />
                            <span style={{ color: '#F2B705' }}>la puissance du réseau.</span>
                        </h2>
                        <p className="text-lg opacity-90 max-w-md leading-relaxed" style={{ color: '#F5F7FA' }}>
                            Un réseau d'apporteurs d'affaires uni par la confiance.
                            Ensemble, créons des opportunités et partageons le succès.
                        </p>
                        
                        {/* Value props */}
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F2B705' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Réseau d'apporteurs d'affaires</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#1E5BFF' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Commissions transparentes</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22C55E' }}></div>
                                <span style={{ color: '#F5F7FA' }} className="text-base font-medium">Croissance partagée</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="text-sm opacity-60" style={{ color: '#F5F7FA' }}>
                        © 2026 Valnexo. Tous droits réservés.
                    </div>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md space-y-8">
                    {/* Mobile Logo */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <ValnexoLogo size={45} textColor="#0B1F3B" />
                        <div>
                            <h1 className="font-bold text-xl" style={{ color: '#0B1F3B' }}>VALNEXO</h1>
                            <p className="text-xs tracking-widest" style={{ color: '#1E5BFF' }}>CRM ÉNERGIE</p>
                        </div>
                    </div>

                    {/* Welcome */}
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#0B1F3B' }}>
                            Connexion
                        </h2>
                        <p className="mt-2 text-slate-600">
                            Accédez à votre espace de gestion
                        </p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500 pr-12"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 text-white font-semibold transition-all hover:opacity-90"
                            style={{ backgroundColor: '#0B1F3B' }}
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                'Se connecter'
                            )}
                        </Button>
                        
                        {/* Mot de passe oublié */}
                        <div className="text-center">
                            <a 
                                href="/forgot-password" 
                                className="text-sm text-blue-600 hover:underline"
                                data-testid="forgot-password-link"
                            >
                                Mot de passe oublié ?
                            </a>
                        </div>

                        {/* Lien de dépannage TOUJOURS visible — bulletproof pour les cas SW zombie
                            où le nouveau code React ne se charge pas. La page /reset.html est un
                            HTML statique servi par nginx, hors SPA, hors SW. */}
                        <div className="text-center pt-1">
                            <a
                                href="/reset.html"
                                className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                                data-testid="reset-app-link"
                            >
                                La connexion ne marche pas ? Réparer l&apos;application
                            </a>
                        </div>

                        {/* Aide contextuelle après 2 échecs : ces cas viennent quasi toujours
                            d'un password manager qui a rempli un ancien mot de passe, d'un
                            copier/coller avec espace parasite, ou d'un Service Worker en
                            cache. On propose 3 actions ciblées avant d'inquiéter le client. */}
                        {failedAttempts >= 2 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="login-help-block">
                                <div className="font-semibold">Toujours bloqué ? Essayez dans l'ordre :</div>
                                <ol className="list-decimal list-inside space-y-1">
                                    <li>Retapez le mot de passe à la main (ne pas copier/coller).</li>
                                    <li>Cliquez sur l'œil pour vérifier ce que le navigateur a rempli.</li>
                                    <li>Ouvrez cette page en <b>navigation privée</b> pour ignorer l'autofill.</li>
                                </ol>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            if ('serviceWorker' in navigator) {
                                                const regs = await navigator.serviceWorker.getRegistrations();
                                                await Promise.all(regs.map((r) => r.unregister()));
                                            }
                                            if ('caches' in window) {
                                                const keys = await caches.keys();
                                                await Promise.all(keys.map((k) => caches.delete(k)));
                                            }
                                            toast.success('Cache vidé. La page va se recharger.');
                                            setTimeout(() => window.location.reload(true), 800);
                                        } catch {
                                            window.location.reload(true);
                                        }
                                    }}
                                    className="underline font-medium hover:text-amber-950"
                                    data-testid="clear-cache-btn"
                                >
                                    Réinitialiser le cache de l'application
                                </button>
                            </div>
                        )}
                        
                        {/* Biometric Login Button - Mobile Only */}
                        {biometricEnabled && isMobile && showBiometricPrompt && (
                            <div className="pt-4 border-t border-slate-200">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full h-12 gap-3 border-slate-300 hover:bg-slate-50"
                                    onClick={handleBiometricLogin}
                                    disabled={biometricLoading}
                                    data-testid="biometric-login-btn"
                                >
                                    {biometricLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Fingerprint className="w-5 h-5 text-blue-600" />
                                            <span>Connexion Face ID / Touch ID</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </form>

                    {/* Footer info */}
                    <p className="text-center text-sm text-slate-500">
                        Problème de connexion ? Contactez votre administrateur.
                    </p>
                    
                    {/* PWA Install hint - Mobile Only */}
                    {isMobile && !window.matchMedia('(display-mode: standalone)').matches && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-start gap-3">
                                <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <p className="font-medium text-blue-900">Installez l'application</p>
                                    <p className="text-blue-700 mt-1">
                                        Ajoutez Valnexo à votre écran d'accueil pour une meilleure expérience.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
